import React, { useState } from 'react'
import { Box, Typography, Icon, Tooltip, Divider } from '@mui/material'
import SectionCard from '../components/createSection'
import InactiveSection from '../components/inactiveSection'
import RespFloatActBtn from '../components/respFloatActBtn'

import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import QuestionCard from '../components/createQuestion'

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import InactiveQuestion from '../components/inactiveQuestion'

import { createForm } from 'services/api/modules/performance/appraisal/form'
import { useMutation, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import { FormatFronttoBack } from 'module/performance/services/helper/formFunctions'
import NewFormNotification from '../../appraisal-company-mgmt/components/NewFormNotification'
import CreateErrorSnackBar from '../components/createErrorSnackBar'
import AdxButton from 'components/custom/AdxButton'

/*
    Bugs to fix:
        [X] when duplicating, sometimes duplicate parameters from different index (?) 
            Solved? -> queskeycount updated too early
        [X] when deleting section, value of deleted section is applied to next section

    To do:
        [X] Restrictions in case if form is for 360
            [X] Disable changing of question type
                [X] Fixed to linear scale
            [X] Form header indicates '360 evaluation form' instead of 'general evaluation form'

        [~] drag and drop
            [ ] make smooth
            [ ] takes 750ms to respond
            [X] drag only enabled when hovering over drag icon
                [X] performance issue when this is done
                    -> pass in dragHandleProps to the child component, in the specific dragHandling area
                    -> janky when inspect element is up, but mostly fine I guess
            [X] handle style while isDragging === true
                [X] make question card translucent
                [X] make question option minimized
                    [X] hide question options
                    [X] make question option size small before dragging
                    -> https://codesandbox.io/s/vertical-list-c8rzn?file=/index.js
        
        [X] UX things
            [X] animated green bar when becoming active
            [X] autofocus on new/modified components

        [X] change some textbox to accept multiple lines
            [X] handled multiline while dragging -> reduced to 1 line

        [X] inactive component
            -> add into currently existing component, handle activity state in index ? 

        [X] Performance Issues
            [X] entering question value -> might have to setTimeout every 200ms

        [X] section functions - need to confirm if any changes
        [X] floating action button functions - need to confirm if any changes
        [X] unique key handling - needs to be performed after API call,
            [X] when loading form, unique key is given following order of question object

        [X] API
            [X] arrange parameters to fit the create API call format
            [X] convert backend to frontend form format
            [X] option textfields are limited to 255 chars
            [X] question titles are limited to 255 chars
            [X] limit linear scale label to 20 chars for aesthetics
            [X] adxInput charLimit not applying current textbox char length on render 
                adxInput has been modified, should reflect length of value in textbox
         
*/

const CreateForm = ({ sx }) => {
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.up('sm'))
  const time = new Date()
  const queryClient = useQueryClient()

  // setInterval(() => {handleSave()}, 30000)

  const navigate = useNavigate()
  const { pathname, state } = useLocation()
  const formId = pathname.split('/').pop()

  const [toPreview, setToPreview] = useState(false)
  // state for whether user is trying to enter preview page or not

  const [quesKeycount, setQuesKeycount] = useState(2)

  const [draggingId, setDraggingId] = useState(null)
  // state used to convert component to dragging state
  const [activeId, setActiveId] = useState(null)
  // used to identify which component is active
  const [activeType, setActiveType] = useState('header')
  // used to identify what type of component is active
  const [activeIndex, setActiveIndex] = useState(null)
  const [formTitle, setFormTitle] = useState('')
  const [formDesc, setFormDesc] = useState('')
  // const [formId, setFormId] = useState("")
  // used to store form's title and description
  const [is360, setIs360] = useState(false)
  // used to identify whether form is exclusively for 360 eval or not
  const [formStructure, setFormStructure] = useState([[0]])
  // key in formStructure will link to the corresponding quesKey in the object array in quesParams
  // formStructure determines the sequence of the question
  // 1st layer array represents the form
  // 2nd layer array represents a section
  // each value inside the array is a question
  // value represents the key of the question, used for mapping and dnd
  const [quesParams, setQuesParams] = useState([
    {
      quesKey: 0,
      quesType: '1',
      quesRequired: true,
      question: '',
      option: [],
    },
  ])
  // default question parameter if not replaced by API call's data, store question parameters
  const [sectParams, setSectParams] = useState([
    {
      title: '',
      desc: '',
    },
  ])
  // default section parameter if not replaced by API call's data, store section parameters

  const matchesMd = useMediaQuery(theme.breakpoints.up('md'))

  const handleActiveFindIndex = (key) => {
    formStructure.forEach((i, indexI) => {
      if (i.indexOf(key) !== -1) {
        setActiveIndex([indexI, i.indexOf(key)])
      }
    })
  }
  // get and keep index of currently active question
  // when form data is fetched, question keys are updated
  // reassign active question after fetch query is done based on index instead of key

  const [createErrorState, setCreateErrorState] = useState({})
  const [createErrorSnackbar, setCreateErrorSnackbar] = useState(false)

  const {
    isLoading: createLoading,
    error: createError,
    mutate: createMutate,
  } = useMutation((values) => createForm(values), {
    onSuccess: async () => {
      navigate('/app/management-centre/company-management/appraisal')
      setCreateErrorState({})
    },
    onError: async (error) => {
      setCreateErrorState(error?.data?.errors)
      setCreateErrorSnackbar(true)
    },
  })

  const iconHoverProp = () => {
    return {
      width: '45px',
      minWidth: '0px',
      height: '45px',
      borderRadius: '50%',
    }
  }
  // props for hover icon

  const handleCreate = () => {
    let newForm = handleFormCompile()

    handleActiveFindIndex(activeId)

    createMutate(newForm)
  }

  const displayCreateError = (error) => {
    if (error === 'title') {
      return 'Form title must be unique'
    } else {
      let tempString = ''
      tempString = `section ${error.section} is empty`
      if (!isNaN(error.question)) {
        tempString = `question ${error.question} of ${tempString}`
      }
      if (!isNaN(error.option)) {
        tempString = `Option ${error.option} for ${tempString}`
      } else {
        tempString = `Title for ${tempString}`
      }
      return tempString
    }
  }

  //========================================================================================================
  // HANDLING FORM FORMAT CONVERSION BACKEND<>FRONTEND
  //========================================================================================================

  const handleFormCompile = () => {
    return FormatFronttoBack(formTitle, formDesc, is360, formStructure, quesParams, sectParams)
  }

  //========================================================================================================
  // GENERAL FUNCTIONS
  //========================================================================================================

  const handleFloatAddQuestion = (sectIndex, quesIndex) => {
    // Adding question with default question parameters

    let items = JSON.parse(JSON.stringify(Array.from(quesParams)))
    items.push({
      quesKey: quesKeycount,
      quesType: is360 ? '2' : '1',
      quesRequired: true,
      question: '',
      option: is360 ? ['1', '5', 'Lowest', 'Highest'] : [''],
    })
    setQuesParams(items)

    // Adding question key at the correct position in form structure
    let form = JSON.parse(JSON.stringify(formStructure))

    quesIndex === undefined
      ? form[sectIndex].splice(quesIndex, 0, quesKeycount)
      : form[sectIndex].splice(quesIndex + 1, 0, quesKeycount)

    handleActive('question', quesKeycount)
    setFormStructure(form)

    setQuesKeycount(quesKeycount + 1)
    //adding to key count to keep question key unique
  }

  const handleFloatAddSection = (sectIndex, quesIndex) => {
    let form = JSON.parse(JSON.stringify(formStructure))
    let sectVal = JSON.parse(JSON.stringify(sectParams))

    if (quesIndex === undefined) {
      form.splice(sectIndex, 0, [])
      sectVal.splice(sectIndex + 1, 0, {
        title: '',
        desc: '',
      })
      //splicing empty section into sectparams and formstructure
      setFormStructure(form)
      setSectParams(sectVal)
      handleActive('section', sectIndex + 1)
    } else {
      form.splice(sectIndex + 1, 0, [])
      let temp = form[sectIndex].splice(quesIndex + 1)
      form[sectIndex + 1].splice(0, 0, ...temp)
      sectVal.splice(sectIndex + 1, 0, {
        title: '',
        desc: '',
      })
      setFormStructure(form)
      setSectParams(sectVal)
      handleActive('section', sectIndex + 1)
    }
  }

  const getQues = (quesKey) => {
    let items = quesParams.find((i) => i.quesKey === quesKey)
    return items
  }
  // used to get question parameter based on key

  const handleActive = (type, target) => {
    setActiveType(type)
    setActiveId(target)
  }
  // set

  //========================================================================================================
  // FORM PARAMETERS
  //========================================================================================================

  const handleFormTitle = (index, value) => {
    setFormTitle(value)
  }

  const handleFormDesc = (index, value) => {
    setFormDesc(value)
  }

  //========================================================================================================
  // SECTION PARAMETERS
  //========================================================================================================

  const handleSectTitle = (index, value) => {
    let items = JSON.parse(JSON.stringify(sectParams))
    items[index].title = value
    setSectParams(items)
  }

  const handleSectDesc = (index, value) => {
    let items = JSON.parse(JSON.stringify(sectParams))
    items[index].desc = value
    setSectParams(items)
  }

  const handleSectDuplicate = (sectIndex) => {
    let form = JSON.parse(JSON.stringify(Array.from(formStructure)))
    let items = JSON.parse(JSON.stringify(Array.from(quesParams)))
    let sectVal = JSON.parse(JSON.stringify(sectParams))
    // duplicate form structure and question parameters
    let duplicatedSect = []
    // empty array to be filled with duplicated question key and pushed into form structure
    let count = quesKeycount
    // duplicating value of keycount
    let sectToDuplicate = JSON.parse(JSON.stringify(Array.from(formStructure[sectIndex - 1])))
    let sectValDuplicate = JSON.parse(JSON.stringify(sectParams[sectIndex - 1]))
    delete sectValDuplicate['id'] // removing id from duplicated section parameter
    // getting keys of questions in section to be duplicated

    sectToDuplicate.forEach((quesKey) => {
      let filteredItem = JSON.parse(JSON.stringify(items.find((x) => x.quesKey === quesKey)))
      filteredItem.quesKey = count
      delete filteredItem['id'] // removing id from duplicated question parameters
      items.push(filteredItem)
      // duplicate question, give new key to duplicate question and push to cloned question parameter
      duplicatedSect.push(count)
      // pushing duplicated question with unique keys into array

      count += 1
    })
    setQuesKeycount(count)
    // assign latest unique key to keycount
    form.splice(sectIndex, 0, duplicatedSect)
    sectVal.splice(sectIndex, 0, sectValDuplicate)
    setFormStructure(form)
    // add duplicated section array + duplicated questions with unique keys into formstructure array
    setQuesParams(items)
    // add duplicated question paramaters into quesparams

    setSectParams(sectVal)
  }

  const handleSectDelete = (sectIndex) => {
    let sectToDelete = JSON.parse(JSON.stringify(Array.from(formStructure[sectIndex - 1])))
    let sect = JSON.parse(JSON.stringify(Array.from(sectParams)))
    let form = JSON.parse(JSON.stringify(Array.from(formStructure)))
    let items = JSON.parse(JSON.stringify(Array.from(quesParams)))
    // deep cloning formstructure and quesparams

    sectToDelete.forEach((quesKey) => {
      items.splice(
        items.findIndex((x) => x.quesKey === quesKey),
        1
      )
      // remove question from cloned quesparams
    })
    sect.splice(sectIndex - 1, 1)
    form.splice(sectIndex - 1, 1)
    // remove section from cloned formstructure
    setSectParams(sect)
    setFormStructure(form)
    setQuesParams(items)
    handleActive(null)
    // assign adjusted values to both states
  }

  const handleSectMove = (direction, index) => {
    let sectPos = JSON.parse(JSON.stringify(formStructure))
    let sectVal = JSON.parse(JSON.stringify(sectParams))
    // deep copy of formstructure and section parameters

    let tempSectPos = sectPos.splice(index, 1)
    let tempSectVal = sectVal.splice(index, 1)
    // splicing out the section parameter and key in form structure

    if (direction === 'up') {
      sectPos.splice(index - 1, 0, ...tempSectPos)
      sectVal.splice(index - 1, 0, ...tempSectVal)
      handleActive('section', index - 1)
    }
    if (direction === 'down') {
      sectPos.splice(index + 1, 0, ...tempSectPos)
      sectVal.splice(index + 1, 0, ...tempSectVal)
      handleActive('section', index + 1)
    }

    setFormStructure(sectPos)
    setSectParams(sectVal)
  }

  //========================================================================================================
  // FORM PARAMETERS
  //========================================================================================================

  const handleQues = (quesKey, param, value) => {
    let items = JSON.parse(
      JSON.stringify(
        quesParams?.splice(
          quesParams?.findIndex((i) => i.quesKey === quesKey),
          1
        )
      )
    )
    //making deep copy of question parameters

    items[0][`${param}`] = value

    if (param === 'quesType') {
      if (value === '2') {
        items[0]['option'] = ['1', '5', '', '']
        //assigning default parameters for linear scale question
      } else {
        items[0]['option'] = ['']
        // assigning default parameters for all question other than linear scale
      }
    }

    let prevState = quesParams.filter((x) => x.quesKey !== quesKey)
    prevState.push(items[0])
    setQuesParams(prevState)
  }

  const handleDuplicate = (sectIndex, quesIndex, quesKey) => {
    // Duplicating question parameters

    let items = JSON.parse(JSON.stringify(Array.from(quesParams)))
    let filteredItem = JSON.parse(JSON.stringify(items.find((x) => x.quesKey === quesKey)))
    filteredItem.quesKey = quesKeycount
    delete filteredItem['id']
    items.push(filteredItem)
    setQuesParams(items)

    //Duplicating question in form structure

    let form = Array.from(formStructure)
    form[sectIndex].splice(quesIndex + 1, 0, quesKeycount)
    setFormStructure(form)

    handleActive('question', quesKeycount)
    setQuesKeycount(quesKeycount + 1)
    //adding to key count to keep question key unique
  }

  const handleQuesDelete = (sectIndex, quesIndex, quesKey) => {
    //Deleting question parameters

    let items = JSON.parse(JSON.stringify(Array.from(quesParams)))
    items.splice(
      items.findIndex((x) => x.quesKey === quesKey),
      1
    )
    setQuesParams(items)

    //Deleting question from form structure

    let form = JSON.parse(JSON.stringify(Array.from(formStructure)))
    form[sectIndex].splice(quesIndex, 1)
    setFormStructure(form)
  }

  //========================================================================================================
  // DRAG & DROP
  //========================================================================================================

  const handleDragEnd = (result) => {
    if (!result.destination) {
      setDraggingId(null)
      return
    }
    // for handling out of bounds dropping

    const items = JSON.parse(JSON.stringify(Array.from(formStructure)))
    const [reorderedItem] = items[parseInt(result.source.droppableId)].splice(
      result.source.index,
      1
    )
    // removing question index reference from formstructure
    items[parseInt(result.destination.droppableId)].splice(
      result.destination.index,
      0,
      reorderedItem
    )
    // replacing question index reference into new position in formstructure
    setFormStructure(items)

    setDraggingId(null)
    // reset dragging ID to null => no object is being dragged
    handleActive('question', draggingId)
    // set the dragged component to be the active component
  }

  const handleBeforeCapture = (result) => {
    setDraggingId(parseInt(result.draggableId))
  }
  //handling of before capture state, processed before dragging starts

  //========================================================================================================
  // RENDER BELOW
  //========================================================================================================

  return (
    <DragDropContext onBeforeCapture={handleBeforeCapture} onDragEnd={handleDragEnd}>
      <CreateErrorSnackBar
        createErrorSnackbar={createErrorSnackbar}
        setCreateErrorSnackbar={setCreateErrorSnackbar}
        message="You have an incomplete field"
        status="error"
        autoHideDuration={4000}
      />
      <NewFormNotification />
      <Box
        sx={{
          backgroundColor: 'gray-background.main',
          minHeight: '100vh',
          maxWidth: '100vw',
          ...sx,
        }}
        display="flex"
        flexDirection="column"
        // gap={4}
      >
        <Box
          sx={{
            width: '100%',
            height: 75,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'white.main',
            borderBottom: '1px solid',
            borderBottomColor: 'gray-line.main',
            position: 'sticky',
            top: 1,
            zIndex: 999,
          }}
          px={'40px'}
        >
          <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
            <Typography variant="h4" noWrap={true}>
              {formTitle ? formTitle : 'Untitled Form'}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center" gap={3}>
            {/* Option 1
            {createError ? (
              <Typography color="error.main">{createError?.data?.message}</Typography>
            ) : null} */}

            {/* Option 2 */}
            {/* {createErrorState.map((i, iIndex) => (
              <Typography color="error.main">{displayCreateError(i)}</Typography>
            ))} */}

            {matches ? (
              <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
                <AdxButton
                  variant={'contained'}
                  sx={{ width: 135 }}
                  onClick={() => handleCreate()}
                  disabled={createLoading ? true : false}
                >
                  {createLoading ? 'Creating...' : 'Create'}
                </AdxButton>
              </Box>
            ) : (
              <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
                <AdxButton
                  variant={'ghost'}
                  onClick={() => handleCreate()}
                  sx={{ ...iconHoverProp() }}
                >
                  <Tooltip title="Save">
                    <Icon>save</Icon>
                  </Tooltip>
                </AdxButton>
              </Box>
            )}
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <Icon
                onClick={() => navigate('/app/management-centre/company-management/appraisal')}
                sx={{ fontSize: 25, cursor: 'pointer' }}
              >
                close
              </Icon>
            </Box>
          </Box>
        </Box>

        {/* logic for adding sections and questions here */}
        {/* Need to add on-click function to cards to determine active/inactive status */}
        <Box
          display="flex"
          flexDirection="column"
          // style={{ marginTop: 50 }}
          px={{ xs: 3, sm: 10, lg: 30 }}
          // pb={{ xs: 15 }}
          py={4}
          gap={3}
        >
          {/* Map for every section created */}
          <Box
            display="flex"
            flexDirection="column"
            sx={{ width: '100%' }}
            gap={2}
            onClick={() => handleActive('header')}
          >
            {activeType === 'header' ? (
              <SectionCard
                sectionName="formHeader"
                formType={is360 ? '360° Evaluation' : 'General Evaluation'}
                handleSectTitle={handleFormTitle}
                handleSectDesc={handleFormDesc}
                sectTitle={formTitle}
                sectDesc={formDesc}
                is360={is360}
                createError={createErrorState['title']}
              ></SectionCard>
            ) : (
              <InactiveSection
                sectionName="formHeader"
                formType={is360 ? '360° Evaluation' : 'General Evaluation'}
                // formLoading={fetchLoading}
                sectTitle={formTitle}
                sectDesc={formDesc}
                is360={is360}
              />
            )}
          </Box>
          <Divider />
          {/*===================================================================================*/}
          {/* Section and Question mapping starts here */}
          {/*===================================================================================*/}

          {formStructure.map((i, indexI) => (
            <Box
              display="flex"
              flexDirection="column"
              sx={{ width: '100%' }}
              gap={1.5}
              key={indexI}
            >
              {indexI === activeId && activeType === 'section' ? (
                <SectionCard
                  sectionNumber={indexI + 1}
                  sectionLength={formStructure.length}
                  handleFloatAddQuestion={handleFloatAddQuestion}
                  handleFloatAddSection={handleFloatAddSection}
                  handleSectTitle={handleSectTitle}
                  handleSectDesc={handleSectDesc}
                  handleSectDuplicate={handleSectDuplicate}
                  handleSectDelete={handleSectDelete}
                  handleSectMove={handleSectMove}
                  sectTitle={sectParams[indexI]?.title || ''}
                  sectDesc={sectParams[indexI]?.desc || ''}
                  createError={createErrorState[`sections.${indexI}.title`]}
                />
              ) : (
                <Box onClick={() => handleActive('section', indexI)}>
                  <InactiveSection
                    sectionNumber={indexI + 1}
                    sectTitle={sectParams[indexI]?.title || ''}
                    sectDesc={sectParams[indexI]?.desc || ''}
                  />
                </Box>
              )}
              <Droppable droppableId={`${indexI}`}>
                {(provided) => (
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ width: '100%' }}
                    gap={1.5}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    direction="vertical"
                  >
                    {i.map((j, indexJ) => (
                      <Draggable key={j} draggableId={`${j}`} index={indexJ}>
                        {(provided, snapshot) => {
                          const draggingStyle = (isDragging) => ({
                            height: isDragging ? '200px' : 'auto',
                            backgroundColor: isDragging ? 'rgba(255,255,255,0.7)' : 'white',
                            boxShadow: isDragging
                              ? '0px 0px 2px lightgray'
                              : '0px 0px 10px lightgray',
                          })
                          //changing style of sxCard when before capture is true
                          //applied before dragging starts
                          return (
                            <Box
                              display="flex"
                              flexDirection="column"
                              sx={{ width: '100%' }}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              {activeId === j && activeType === 'question' ? (
                                <QuestionCard
                                  handleDuplicate={handleDuplicate}
                                  handleQuesDelete={handleQuesDelete}
                                  handleQues={handleQues}
                                  handleFloatAddQuestion={handleFloatAddQuestion}
                                  handleFloatAddSection={handleFloatAddSection}
                                  valueType={getQues(j)?.quesType || '1'}
                                  valueRequired={getQues(j)?.quesRequired || false}
                                  valueQuestion={getQues(j)?.question || ''}
                                  valueOption={snapshot.isDragging ? [] : getQues(j)?.option || []}
                                  is360={is360}
                                  quesKey={j}
                                  sectIndex={indexI}
                                  quesIndex={indexJ}
                                  activeId={activeId}
                                  sxCard={draggingStyle(j === draggingId)}
                                  //overwrite sxCard when is true => handled before capture

                                  handleDrag={provided.dragHandleProps}
                                  isDragging={snapshot.isDragging}
                                  // handling placeholder while dragging
                                  createErrorState={createErrorState}
                                  createError={`sections.${indexI}.questions.${indexJ}.`}
                                />
                              ) : (
                                <Box onClick={() => handleActive('question', j)}>
                                  <InactiveQuestion
                                    valueType={getQues(j)?.quesType || '1'}
                                    valueRequired={getQues(j)?.quesRequired || false}
                                    valueQuestion={getQues(j)?.question || ''}
                                    valueOption={getQues(j)?.option || []}
                                    sxCard={draggingStyle(j === draggingId)}
                                    handleDrag={provided.dragHandleProps}
                                    isDragging={snapshot.isDragging}
                                  />
                                </Box>
                              )}
                            </Box>
                          )
                        }}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    <br />
                  </Box>
                )}
              </Droppable>
            </Box>
          ))}
        </Box>
        {matchesMd ? null : (
          <RespFloatActBtn
            handleFloatAddQuestion={handleFloatAddQuestion}
            handleFloatAddSection={handleFloatAddSection}
            handleSectMove={handleSectMove}
            formStructure={formStructure}
            activeType={activeType}
            activeId={activeId}
          />
        )}
      </Box>
    </DragDropContext>
  )
}

export default CreateForm
